<!--
@component
Calendar

Wraps `@svelte-plugins/datepicker`. All date math goes through `$utils/dates`
so the calendar can never disagree with the rest of the schedule UI about
which local calendar day an ISO string belongs to.
-->

<script lang="ts">
  import { DatePicker } from '@svelte-plugins/datepicker';
  import { appState } from '$lib/stores/index.svelte';
  import { formatDateMDY } from '$utils/dates';

  let {
    dates = $bindable(),
    activeDate = $bindable(),
  }: {
    /** Canonical UTC ISO strings — one per day with matches. */
    dates: string[];
    activeDate: Date | undefined;
  } = $props();

  /** Sorted timestamps for the tournament's date range (ascending). */
  const sortedTimes = $derived(
    dates.map((d) => new Date(d).getTime()).sort((a, b) => a - b)
  );

  const firstDate = $derived(new Date(sortedTimes[0]));
  const lastDate = $derived(new Date(sortedTimes[sortedTimes.length - 1]));

  const startMonth = $derived(firstDate.getMonth());
  const endMonth = $derived(lastDate.getMonth());
  const startYear = $derived(firstDate.getFullYear());
  const endYear = $derived(lastDate.getFullYear());

  /** `enabledDates` for the picker — local-TZ MM/DD/YYYY strings. */
  const enabledDates = $derived(dates.map(formatDateMDY));
  const defaultYear = $derived(startYear);

  /**
   * Open the calendar on the month that's most relevant *right now*:
   * - If today falls within the tournament window, show today's month.
   * - If today is before the tournament starts, show the first month.
   * - If today is after the tournament ends, show the last month.
   *
   * Comparing full year-months (year * 12 + month) avoids the bug where
   * a tournament spanning a year boundary would compare months in
   * isolation (e.g. Dec 2025 vs. Jan 2026).
   */
  const defaultMonth = $derived.by(() => {
    const now = new Date();
    const nowYM = now.getFullYear() * 12 + now.getMonth();
    const startYM = startYear * 12 + startMonth;
    const endYM = endYear * 12 + endMonth;
    if (nowYM < startYM) return startMonth;
    if (nowYM > endYM) return endMonth;
    return now.getMonth();
  });

  /** The date currently bound to the DatePicker's `startDate`. */
  let startDate = $state<string | Date>('');

  /**
   * The currently-visible year-month, as set by user navigation.
   * `undefined` means "fall back to the derived default", which keeps
   * the nav-button visibility correct if `dates` (and therefore
   * `defaultMonth` / `defaultYear`) change before the user navigates.
   *
   * Year and month are tracked together so they can't drift out of sync.
   */
  let visiblePeriod = $state<{ month: number; year: number } | undefined>();

  const visibleMonth = $derived(visiblePeriod?.month ?? defaultMonth);
  const visibleYear = $derived(visiblePeriod?.year ?? defaultYear);

  const visibleYM = $derived(visibleYear * 12 + visibleMonth);
  const startYM = $derived(startYear * 12 + startMonth);
  const endYM = $derived(endYear * 12 + endMonth);

  const prevMonthNav = $derived(visibleYM <= startYM ? 'hidden' : 'visible');
  const nextMonthNav = $derived(visibleYM >= endYM ? 'hidden' : 'visible');

  /**
   * Toggle day selection: clicking the active day clears the filter;
   * clicking any other enabled day sets it.
   */
  const handleDayClick = (e: { startDate: number }) => {
    const activeTime = activeDate ? new Date(activeDate).getTime() : null;
    if (activeTime === e.startDate) {
      startDate = '';
      activeDate = undefined;
    } else {
      startDate = new Date(e.startDate);
      activeDate = startDate;
    }
  };

  /** Track which month is currently shown to drive nav visibility. */
  const handleNavigationChange = (e: {
    currentPeriod: { start: Date | string; end: Date | string };
  }) => {
    const start = new Date(e.currentPeriod.start);
    visiblePeriod = { month: start.getMonth(), year: start.getFullYear() };
  };
</script>

<div
  class="match-calendar relative"
  class:loading={appState.isLoading}
  style="--prev:{prevMonthNav}; --next:{nextMonthNav}"
>
  <DatePicker
    bind:startDate
    {defaultYear}
    {enabledDates}
    {defaultMonth}
    enablePastDates={true}
    enableFutureDates={true}
    isOpen={true}
    isMultipane={false}
    showYearControls={false}
    includeFont={false}
    theme="custom-datepicker"
    onDayClick={handleDayClick}
    onNavigationChange={handleNavigationChange}
  ></DatePicker>
</div>

<style lang="scss">
  @use '../../../styles/mixins' as mixins;

  /* Uses https://github.com/svelte-plugins/datepicker */
  :global(.datepicker[data-picker-theme='custom-datepicker']) {
    // Font overrides
    --datepicker-container-font-family: var(--theme-font-family-sans-serif);

    --datepicker-calendar-day-font-family: var(--theme-font-family-sans-serif);

    --datepicker-calendar-dow-font-size: var(--theme-font-size-xs);

    --datepicker-calendar-day-font-size: var(--theme-font-size-xs);

    --datepicker-calendar-header-text-font-size: var(--theme-font-size-xs);

    --datepicker-calendar-range-selected-font-weight: 700;

    // Colour overrides
    --datepicker-container-background: transparent;

    --datepicker-border-color: transparent;

    --datepicker-container-border: none;

    --datepicker-calendar-day-color: var(--theme-colour-text-primary);

    --datepicker-calendar-day-color-disabled: var(
      --theme-colour-text-secondary
    );

    --datepicker-calendar-day-background-hover: var(--theme-colour-white);

    --datepicker-calendar-range-selected-background: var(--theme-colour-white);

    --datepicker-calendar-range-selected-color: var(--theme-colour-logo);

    --datepicker-calendar-day-color-hover: var(--theme-colour-logo);

    // Style overrides
    --datepicker-container-position: relative;

    --datepicker-calendar-width: auto;

    --datepicker-container-zindex: 0;

    --datepicker-container-box-shadow: none;

    --datepicker-calendar-range-border: 2px solid var(--theme-colour-logo);

    --datepicker-calendar-today-border: 1px solid var(--theme-colour-logo);

    // --datepicker-calendar-today-background: var(--theme-colour-muted-grey);

    --datepicker-calendar-day-container-margin: 2px;

    --datepicker-calendar-padding: 0px;
  }

  .match-calendar {
    display: block;
    position: relative;
    // background: linear-gradient(
    //   to right,
    //   var(--theme-colour-background) 10%,
    //   var(--theme-colour-muted-grey) 50%,
    //   var(--theme-colour-background) 90%
    // );

    &.loading {
      pointer-events: none;
      :global(.calendar::before) {
        content: '';
        position: absolute;
        left: 0;
        top: 0;
        border-radius: var(--theme-style-border-radius);
        mix-blend-mode: multiply;
        width: 100%;
        height: 100%;
        // @include mixins.loading;
      }
    }

    :global(.calendars-container) {
      display: block !important;
      margin-inline: auto;

      @include mixins.hard-shadow;
    }

    :global(.datepicker button:not(.disabled) span:hover) {
      @include mixins.hard-shadow;
      border: 1px solid var(--theme-colour-logo) !important;
    }
    :global(.datepicker button.start.end) {
      @include mixins.hard-shadow;
    }

    :global(.date:not(.disabled)) {
      @include mixins.font-medium;
      font-variant-numeric: tabular-nums;
    }

    :global(.date:not(.disabled) span) {
      background: var(--theme-colour-muted-grey);
    }

    :global(.date.today:not(.disabled) span) {
      background: var(--theme-colour-muted-grey);
    }
    :global(.date.today:not(.disabled) span) {
      cursor: pointer !important;
    }

    // Month nav
    :global(header button:first-of-type) {
      visibility: var(--prev);
    }
    :global(header button:last-of-type) {
      visibility: var(--next);
    }
  }
</style>
